import { useEffect, useRef, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { titleForPath } from "./documentTitle";

/**
 * What happens when the address changes, for every route in the table.
 *
 * Two faults, both from reviewer D on 2026-09-08, and both of them properties of the frame rather
 * than of any one page, which is why they are answered once here instead of eight times in eight
 * components.
 *
 * `document.title` was never written, so every destination shared the name in `index.html`.
 *
 * And `<main id="main-content" tabIndex={-1}>` existed in the shell with nothing ever moving focus
 * to it. A `pushState` replaces the content and tells assistive technology nothing at all: the
 * client activates Ärenden in the rail, the page changes, focus stays on the link, and a screen
 * reader announces nothing. Moving focus into the new page is what makes the change audible, and it
 * is why the element was given a `tabIndex` in the first place.
 */
export function RouteChrome({ children }: { children?: ReactNode }) {
  const location = useLocation();
  const firstRender = useRef(true);

  useEffect(() => {
    document.title = titleForPath(location.pathname);
  }, [location.pathname]);

  /**
   * Keyed on `location.key` rather than on the pathname, because two visits to the same address are
   * two navigations and the second one has to land as well as the first.
   *
   * The first render is skipped. Arriving at a portal is not moving inside it, and taking focus off
   * whatever the browser restored, or off nothing at all, would be a change nobody asked for.
   *
   * The check on `document.activeElement` is the important half. Effects run child first and parent
   * second, so this runs after the routed page's own effects have finished. A page that deliberately
   * moved focus into its own content has already done so by now, which is exactly what the ärende
   * page does on a narrow screen when a request is opened, and stealing it back here would undo that
   * one frame later. So the rule is: focus moves only when the page has not claimed it. The scroll
   * reset follows the same condition, for the same reason. A page that scrolled itself to the thing
   * it was asked for does not want to be returned to the top.
   */
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    const main = document.getElementById("main-content");
    if (!main) {
      return;
    }

    const active = document.activeElement;
    if (active && active !== document.body && active !== main && main.contains(active)) {
      return;
    }

    // `preventScroll`, because the scroll position is set on the next line and letting the focus
    // call scroll first would be two answers to one question.
    main.focus({ preventScroll: true });
    main.scrollTop = 0;
  }, [location.key]);

  /**
   * An address with a `#`, scrolled to once the element exists. Added 2026-09-22, when the overview's
   * attention rows began linking to one panel on `/settings`: the panels there render after their
   * own reads, so the element is often not in the document on the render that changed the address.
   * It is looked for once a frame for up to three seconds and then given up on, which leaves the
   * page at its top, the behaviour before this existed.
   */
  useEffect(() => {
    const id = decodeURIComponent(location.hash.replace(/^#/, ""));
    if (!id) {
      return;
    }

    let frame = 0;
    const deadline = Date.now() + 3_000;

    const look = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ block: "start" });
        return;
      }
      if (Date.now() < deadline) {
        frame = window.requestAnimationFrame(look);
      }
    };

    frame = window.requestAnimationFrame(look);
    return () => window.cancelAnimationFrame(frame);
  }, [location.key, location.hash]);

  return <>{children}</>;
}
